import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator, ImageNotSupported } from '@mui/icons-material';

const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        alignItems: 'stretch',
        gap: '12px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        transition: 'all 0.2s',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderColor: '#999',
        },
    },
    dragging: {
        opacity: 0.5,
        backgroundColor: '#f5f5f5',
    },
    dragHandle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '48px',
        backgroundColor: '#f5f5f5',
        cursor: 'grab',
        color: '#999',
        '&:active': {
            cursor: 'grabbing',
        },
    },
    imageSection: {
        width: '120px',
        minWidth: '120px',
        height: '120px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    contentSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '12px 16px',
        minWidth: 0,
    },
    title: {
        fontFamily: "'GucciSans-Bold' !important",
        fontSize: '14px',
        fontWeight: 'bold',
        marginBottom: '8px',
        wordBreak: 'break-word',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    details: {
        fontSize: '12px',
        color: '#666',
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    orderBadge: {
        display: 'inline-block',
        backgroundColor: '#000',
        color: '#fff',
        padding: '4px 8px',
        borderRadius: '4px',
        fontFamily: "'GucciSans-Bold' !important",
        fontSize: '11px',
        fontWeight: 'bold',
    },
}));

export default function SortableImageItem({ image, index, isDragging = false }) {
    const classes = useStyles();
    const [imageError, setImageError] = useState(false);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({ id: image.id });

    const style = {
        transform: CSS.Transform.toString(transform) || undefined,
        transition: typeof transition === 'string' ? transition : undefined,
    };

    const isImageDragging = isDragging || isSortableDragging;

    return (
        <Box
            ref={setNodeRef}
            style={style}
            className={`${classes.container} ${isImageDragging ? classes.dragging : ''}`}
        >
            <Box className={classes.dragHandle} {...attributes} {...listeners}>
                <DragIndicator />
            </Box>

            <Box className={classes.imageSection}>
                {!imageError && (image.thumbUrl || image.imageUrl) ? (
                    <img
                        src={image.thumbUrl || image.imageUrl}
                        alt={image.name}
                        className={classes.image}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <Box sx={{ textAlign: 'center', color: '#ccc' }}>
                        <ImageNotSupported sx={{ fontSize: '32px' }} />
                    </Box>
                )}
            </Box>

            <Box className={classes.contentSection}>
                <Typography className={classes.title}>
                    {image.name}
                </Typography>
                <Box className={classes.details}>
                    <span className={classes.orderBadge}>
                        Order: {index + 1}
                    </span>
                </Box>
            </Box>
        </Box>
    );
}